import React from 'react';
import { currentUserId } from './useCurrentUserId';

/**
 * Per-browser inbox preferences: stars, pins, archive, labels, drafts, quick
 * replies, and the quotes on our own replies.
 *
 * ★ THIS IS A STOPGAP AND SAYS SO. The server has no field for any of these
 * yet, so they live in localStorage — on this device, for this user, for this
 * tenant. Every surface that shows one carries "on this device" in its
 * tooltip, so nobody expects a star to follow them to another laptop. When the
 * backend grows the fields, this module is the only thing that changes.
 *
 * One JSON blob per (tenant, user), read once and kept in memory; a module
 * store with listeners rather than context, because the list, the header and
 * the composer all read it and none of them share a parent that should
 * re-render on a draft keystroke.
 */

const DEFAULTS = {
	starred: {},
	pinned: {},
	archived: {},
	labels: {},
	drafts: {},
	quickReplies: [
		{
			id: 'qr-hello',
			shortcut: 'hello',
			text: 'Hello! Thanks for contacting us. How can I help you today?',
		},
		{
			id: 'qr-shortly',
			shortcut: 'shortly',
			text: 'Thank you. I will get back to you shortly with the details.',
		},
		{ id: 'qr-thanks', shortcut: 'thanks', text: 'Thank you for your time. Have a great day!' },
	],
	quotes: {},
};

/** The conversation labels on offer. Colour schemes, not hex — Chakra's own scales. */
export const LABELS = [
	{ id: 'new', name: 'New customer', scheme: 'green' },
	{ id: 'followup', name: 'Follow up', scheme: 'orange' },
	{ id: 'hot', name: 'Hot', scheme: 'red' },
	{ id: 'pending', name: 'Pending payment', scheme: 'blue' },
	{ id: 'closed', name: 'Closed', scheme: 'gray' },
];

const listeners = new Set();
let cache = null;
let cacheKey = null;

const storageKey = () => {
	const tenant =
		localStorage.getItem('tenantId') || sessionStorage.getItem('tenantId') || 'tenant';
	return `wa2:${tenant}:${currentUserId() || 'anon'}:prefs`;
};

export function readPrefs() {
	const key = storageKey();
	if (cache && cacheKey === key) return cache;
	let stored = null;
	try {
		stored = JSON.parse(localStorage.getItem(key) || 'null');
	} catch (_) {
		stored = null;
	}
	cache = { ...DEFAULTS, ...(stored || {}) };
	cacheKey = key;
	return cache;
}

export function writePrefs(mutate) {
	const next = mutate({ ...readPrefs() });
	cache = next;
	try {
		localStorage.setItem(storageKey(), JSON.stringify(next));
	} catch (_) {
		// Storage full or blocked: the in-memory copy still works for this tab.
	}
	listeners.forEach((l) => l(next));
	return next;
}

/** Subscribe. React 17 has no useSyncExternalStore; a listener set does the job. */
export function usePrefs() {
	const [state, setState] = React.useState(readPrefs);
	React.useEffect(() => {
		listeners.add(setState);
		return () => listeners.delete(setState);
	}, []);
	return state;
}

const toggleIn = (map, id) => {
	const next = { ...map };
	if (next[id]) delete next[id];
	else next[id] = Date.now();
	return next;
};

export const prefs = {
	toggleStar: (wamid) => writePrefs((p) => ({ ...p, starred: toggleIn(p.starred, wamid) })),
	togglePin: (conversationId) =>
		writePrefs((p) => ({ ...p, pinned: toggleIn(p.pinned, conversationId) })),
	toggleArchive: (conversationId) =>
		writePrefs((p) => ({ ...p, archived: toggleIn(p.archived, conversationId) })),
	toggleLabel: (conversationId, labelId) =>
		writePrefs((p) => {
			const current = p.labels[conversationId] ?? [];
			const next = current.includes(labelId)
				? current.filter((l) => l !== labelId)
				: [...current, labelId];
			return { ...p, labels: { ...p.labels, [conversationId]: next } };
		}),
	setDraft: (conversationId, text) =>
		writePrefs((p) => {
			const drafts = { ...p.drafts };
			if (text && text.trim()) drafts[conversationId] = text;
			else delete drafts[conversationId];
			return { ...p, drafts };
		}),
	saveQuickReply: (reply) =>
		writePrefs((p) => {
			const list = p.quickReplies.filter((r) => r.id !== reply.id);
			return { ...p, quickReplies: [...list, reply] };
		}),
	removeQuickReply: (id) =>
		writePrefs((p) => ({ ...p, quickReplies: p.quickReplies.filter((r) => r.id !== id) })),
	/** Our reply's quote, remembered so it survives the server dropping it. */
	rememberQuote: (wamid, quotedWamid) =>
		writePrefs((p) => ({ ...p, quotes: { ...p.quotes, [wamid]: quotedWamid } })),
};
